import { init } from '@noriginmedia/norigin-spatial-navigation';
import { useCallback, useEffect, useState } from 'react';

import { getMyProfile, getResources, syncSubscriptions } from '@/api';
import { i18n } from '@/app/_providers/i18n';
import { ClientAuth, Device, Logger, RequestFailedError, Store, UnauthorizedError } from '@/classes';
import { FallbackReason } from '@/shared/ui';
import { checkCanAutoPlay } from '@/utils';

type InitializeStatus = 'loading' | 'ready' | 'error';

export const useAppInitialize = () => {
  const [status, setStatus] = useState<InitializeStatus>('loading');
  const [reason, setReason] = useState<FallbackReason>('network');

  const initialize = useCallback(async () => {
    setStatus('loading');

    Device.loadConfig();
    i18n.changeLanguage(Device.language);

    const rootElement = document.getElementById('html-root');
    if (rootElement) {
      rootElement.lang = Device.language;
    }

    try {
      const resources = await getResources({ language: Device.language });
      Store.set('resources', resources);
    } catch (e) {
      console.error('리소스 로드 오류', e);
      const isServerError = e instanceof RequestFailedError || e instanceof UnauthorizedError;

      setReason(isServerError ? 'unknown' : 'network');
      setStatus('error');
      return;
    }

    await checkCanAutoPlay();

    try {
      const isLoggedIn = await ClientAuth.refresh();

      if (isLoggedIn) {
        await syncSubscriptions();
        ClientAuth.user = await getMyProfile();
      }
    } catch (e) {
      console.error('사용자 정보 조회 오류', e);
    }

    try {
      Logger.loadLogs();
      Logger.ubid();
      Logger.startApp();
    } catch (e) {
      console.error('로거 초기화 오류', e);
    }

    setStatus('ready');
  }, []);

  useEffect(() => {
    const handleUnload = () => Logger.tts();

    window.addEventListener('beforeunload', handleUnload);

    return () => {
      window.removeEventListener('beforeunload', handleUnload);
    };
  }, []);

  useEffect(() => {
    init({
      debug: false,
      visualDebug: false,
      shouldFocusDOMNode: true,
      // ! keydown 스로틀. 리모컨을 누르고 있을 때(auto-repeat)만 걸리고, 떼었다 누르면 keyup 이 취소한다.
      // ! 500 이면 홀드 시 초당 2회만 이동해 굼뜨다.
      throttle: 150,
    });
    initialize();
  }, [initialize]);

  return { status, reason, retry: initialize };
};
